import { useForm, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Box } from '@mui/material';
import useLocalization from '../../../hooks/useLocalization';
import { FormTextField } from '../../molecules/form-elements';
import PhoneNumberInput from '../../atoms/PhoneNumberInput';
import StyledForm from '../shared/StyledForm';
import { useLigdiCash } from '../../../hooks/payment/useLigdiCash';
import { TFunction } from 'i18next';
import { useBalances } from '../../../queries';
import { createPhoneSchema, normalizePhoneNumber } from '../../../schemas/helpers/phoneValidation';

const styles = {
  stepContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
};

const withdrawSchema = (t: TFunction, walletBalance: number) => {
  return z.object({
    amount: z.coerce
      .number({ error: t('invalidNumber') })
      .int({ error: t('noDecimalNumbersAllowed') })
      .max(walletBalance, t('insufficientFunds')),
    phone_number: createPhoneSchema(t, true),
  });
};

type WithdrawFormInput = z.input<ReturnType<typeof withdrawSchema>>;
type WithdrawFormData = z.output<ReturnType<typeof withdrawSchema>>;

const LigdiCashWithdrawForm = ({ paymentProvider }: { paymentProvider: string | null }) => {
  const { t } = useLocalization();
  const { mainBalance: { balance: walletBalance } = { balance: 0 } } = useBalances();

  const {
    withdrawMutation: { mutate: withdraw, isPending: isWithdrawPending },
  } = useLigdiCash(paymentProvider);

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<WithdrawFormInput, unknown, WithdrawFormData>({
    resolver: zodResolver(withdrawSchema(t, walletBalance)),
    defaultValues: {
      amount: undefined,
      phone_number: '+226',
    },
  });

  const handleWithdrawSubmit: SubmitHandler<WithdrawFormData> = (data) => {
    withdraw({ ...data, phone_number: normalizePhoneNumber(data.phone_number) });
  };

  return (
    <StyledForm
      submitTitle={t('continue')}
      onSubmit={handleSubmit(handleWithdrawSubmit)}
      isSubmitting={isWithdrawPending}
      disablePadding
    >
      <Box sx={styles.stepContainer}>
        <FormTextField
          name='amount'
          control={control}
          label={t('amount')}
          fullWidth
          type='number'
          error={!!errors.amount}
          helperText={errors.amount?.message}
          disabled={isWithdrawPending}
          inputMode='numeric'
        />
        <PhoneNumberInput
          name='phone_number'
          control={control}
          error={!!errors.phone_number}
          helperText={errors.phone_number?.message}
          disabled={isWithdrawPending}
        />
      </Box>
    </StyledForm>
  );
};

export default LigdiCashWithdrawForm;
